import { useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Select,
  DataTable,
  Spinner,
  Banner,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface RevenueStats {
  mrr: number;
  arr: number;
  totalRevenue: number;
  averageRevenuePerShop: number;
  paidShops: number;
  freeShops: number;
  conversionRate: number;
}

interface MonthlyData {
  month: string;
  newMrr: number;
  churnedMrr: number;
  netMrr: number;
  newShops: number;
  churnedShops: number;
  totalMrr: number;
}

interface GrowthStats {
  shopGrowthRate: number;
  revenueGrowthRate: number;
  churnRate: number;
  netRevenueRetention: number;
  monthlyData: MonthlyData[];
}

export default function Revenue() {
  const { fetchApi, error } = useApi();
  const [revenueStats, setRevenueStats] = useState<RevenueStats | null>(null);
  const [growthStats, setGrowthStats] = useState<GrowthStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('6');

  useEffect(() => {
    loadData();
  }, [period]);

  async function loadData() {
    setLoading(true);

    const [revenueResponse, growthResponse] = await Promise.all([
      fetchApi<{ success: boolean; stats: RevenueStats }>('/super-admin/analytics/revenue'),
      fetchApi<{ success: boolean; stats: GrowthStats }>(
        `/super-admin/analytics/growth?months=${period}`
      ),
    ]);

    if (revenueResponse?.success) {
      setRevenueStats(revenueResponse.stats);
    }

    if (growthResponse?.success) {
      setGrowthStats(growthResponse.stats);
    }

    setLoading(false);
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const monthlyRows = growthStats?.monthlyData.map((month) => [
    month.month,
    formatCurrency(month.newMrr),
    formatCurrency(month.churnedMrr),
    <Text
      key={month.month}
      as="span"
      tone={month.netMrr >= 0 ? 'success' : 'critical'}
    >
      {formatCurrency(month.netMrr)}
    </Text>,
    `+${month.newShops}`,
    `-${month.churnedShops}`,
    formatCurrency(month.totalMrr),
  ]) || [];

  if (loading) {
    return (
      <Page title="Revenue Analytics">
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack align="center">
                <Spinner size="large" />
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title="Revenue Analytics" subtitle="Track your SaaS metrics">
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Key Metrics */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">MRR</Text>
                <Text as="p" variant="heading2xl">
                  {formatCurrency(revenueStats?.mrr || 0)}
                </Text>
                <Text as="p" tone="subdued" variant="bodySm">
                  Monthly Recurring Revenue
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">ARR</Text>
                <Text as="p" variant="heading2xl">
                  {formatCurrency(revenueStats?.arr || 0)}
                </Text>
                <Text as="p" tone="subdued" variant="bodySm">
                  Annual Recurring Revenue
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">ARPU</Text>
                <Text as="p" variant="heading2xl">
                  {formatCurrency(revenueStats?.averageRevenuePerShop || 0)}
                </Text>
                <Text as="p" tone="subdued" variant="bodySm">
                  Avg Revenue per Shop
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">Conversion</Text>
                <Text as="p" variant="heading2xl">
                  {(revenueStats?.conversionRate || 0).toFixed(1)}%
                </Text>
                <Text as="p" tone="subdued" variant="bodySm">
                  Free to Paid Rate
                </Text>
              </BlockStack>
            </Card>
          </InlineStack>
        </Layout.Section>

        {/* Growth Metrics */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">Shop Growth</Text>
                <InlineStack gap="200" blockAlign="center">
                  <Text as="p" variant="headingLg">
                    {formatPercent(growthStats?.shopGrowthRate || 0)}
                  </Text>
                  <Badge tone={(growthStats?.shopGrowthRate || 0) >= 0 ? 'success' : 'critical'}>
                    MoM
                  </Badge>
                </InlineStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">Revenue Growth</Text>
                <InlineStack gap="200" blockAlign="center">
                  <Text as="p" variant="headingLg">
                    {formatPercent(growthStats?.revenueGrowthRate || 0)}
                  </Text>
                  <Badge tone={(growthStats?.revenueGrowthRate || 0) >= 0 ? 'success' : 'critical'}>
                    MoM
                  </Badge>
                </InlineStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">Churn Rate</Text>
                <InlineStack gap="200" blockAlign="center">
                  <Text as="p" variant="headingLg">
                    {(growthStats?.churnRate || 0).toFixed(1)}%
                  </Text>
                  <Badge tone={(growthStats?.churnRate || 0) <= 5 ? 'success' : 'warning'}>
                    Monthly
                  </Badge>
                </InlineStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">Net Revenue Retention</Text>
                <InlineStack gap="200" blockAlign="center">
                  <Text as="p" variant="headingLg">
                    {(growthStats?.netRevenueRetention || 0).toFixed(0)}%
                  </Text>
                  <Badge tone={(growthStats?.netRevenueRetention || 0) >= 100 ? 'success' : 'warning'}>
                    NRR
                  </Badge>
                </InlineStack>
              </BlockStack>
            </Card>
          </InlineStack>
        </Layout.Section>

        {/* Customer Breakdown */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingLg">Customer Breakdown</Text>
              <InlineStack gap="800">
                <BlockStack gap="100">
                  <Text as="p" variant="headingLg" tone="success">
                    {revenueStats?.paidShops || 0}
                  </Text>
                  <Text as="p" tone="subdued">Paid Shops</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="p" variant="headingLg">
                    {revenueStats?.freeShops || 0}
                  </Text>
                  <Text as="p" tone="subdued">Free Shops</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="p" variant="headingLg">
                    {(revenueStats?.paidShops || 0) + (revenueStats?.freeShops || 0)}
                  </Text>
                  <Text as="p" tone="subdued">Total Active</Text>
                </BlockStack>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Monthly Trend Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingLg">Monthly Revenue Trend</Text>
                <Select
                  label="Period"
                  labelInline
                  options={[
                    { label: '3 months', value: '3' },
                    { label: '6 months', value: '6' },
                    { label: '12 months', value: '12' },
                  ]}
                  value={period}
                  onChange={setPeriod}
                />
              </InlineStack>

              {monthlyRows.length > 0 ? (
                <DataTable
                  columnContentTypes={[
                    'text',
                    'numeric',
                    'numeric',
                    'numeric',
                    'numeric',
                    'numeric',
                    'numeric',
                  ]}
                  headings={[
                    'Month',
                    'New MRR',
                    'Churned MRR',
                    'Net MRR',
                    'New Shops',
                    'Churned',
                    'Total MRR',
                  ]}
                  rows={monthlyRows}
                />
              ) : (
                <Text as="p" tone="subdued">
                  No revenue data available yet
                </Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Health Indicators */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingLg">Business Health</Text>
              <InlineStack gap="400">
                <HealthIndicator
                  label="LTV:CAC Ratio"
                  value="N/A"
                  status="info"
                  description="Need CAC data"
                />
                <HealthIndicator
                  label="Churn"
                  value={`${(growthStats?.churnRate || 0).toFixed(1)}%`}
                  status={(growthStats?.churnRate || 0) <= 5 ? 'good' : (growthStats?.churnRate || 0) <= 10 ? 'warning' : 'bad'}
                  description={
                    (growthStats?.churnRate || 0) <= 5
                      ? 'Healthy churn rate'
                      : (growthStats?.churnRate || 0) <= 10
                        ? 'Churn needs attention'
                        : 'High churn - investigate'
                  }
                />
                <HealthIndicator
                  label="NRR"
                  value={`${(growthStats?.netRevenueRetention || 0).toFixed(0)}%`}
                  status={(growthStats?.netRevenueRetention || 0) >= 100 ? 'good' : 'warning'}
                  description={
                    (growthStats?.netRevenueRetention || 0) >= 100
                      ? 'Revenue expanding'
                      : 'Revenue contracting'
                  }
                />
                <HealthIndicator
                  label="Growth"
                  value={formatPercent(growthStats?.revenueGrowthRate || 0)}
                  status={(growthStats?.revenueGrowthRate || 0) > 0 ? 'good' : 'warning'}
                  description={
                    (growthStats?.revenueGrowthRate || 0) > 10
                      ? 'Strong growth'
                      : (growthStats?.revenueGrowthRate || 0) > 0
                        ? 'Steady growth'
                        : 'Declining revenue'
                  }
                />
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function HealthIndicator({
  label,
  value,
  status,
  description,
}: {
  label: string;
  value: string;
  status: 'good' | 'warning' | 'bad' | 'info';
  description: string;
}) {
  const statusColors = {
    good: { bg: '#e3f1df', border: '#1a7f37' },
    warning: { bg: '#fff8e5', border: '#b98900' },
    bad: { bg: '#fff0f0', border: '#d72c0d' },
    info: { bg: '#f0f6ff', border: '#0068d6' },
  };

  const colors = statusColors[status];

  return (
    <div
      style={{
        flex: 1,
        padding: '1rem',
        background: colors.bg,
        borderLeft: `4px solid ${colors.border}`,
        borderRadius: '4px',
      }}
    >
      <BlockStack gap="100">
        <Text as="p" variant="bodySm" fontWeight="semibold">
          {label}
        </Text>
        <Text as="p" variant="headingMd">
          {value}
        </Text>
        <Text as="p" variant="bodySm" tone="subdued">
          {description}
        </Text>
      </BlockStack>
    </div>
  );
}
